import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import CircularProgress from '@material-ui/core/CircularProgress';

import Modal from '../../shared/modal';
import Button from '../../shared/Button';
import Checkbox from '../../shared/Checkbox';

import { exportUsageToUnleashed, getCase, getUnleashedSalesOrder } from '../../../actions/casesActions';

import { useAlert } from '../../../hooks';

import { usageExportTypes } from '../../../constants/enums';

import ExportUsageUnleashed from './ExportUsageUnleashed';

const options = [
  { key: 0, label: 'Detailed Usage Export (inc. case info & images in .xlsx format)', value: usageExportTypes.DEFAULT_EXCEL },
  { key: 1, label: 'Usage Only Export (single-sheet .csv format)', value: usageExportTypes.DEFAULT_CSV },
  { key: 2, label: 'Unleashed Sales Order Export (single-sheet .csv format)', value: usageExportTypes.UNLEASHED_CSV },
  { key: 3, label: 'CIN7 Usage Export (.xlsx)', value: usageExportTypes.CIN_7 },
];

const ExportUsageModal = (props) => {
  const {
    open,
    onClose,
    onSubmit,
    loading,
    lastSelectedUsageExportType,
    activeCase,
    users = []
  } = props;

  const dispatch = useDispatch();
  const { showAlert } = useAlert();

  const [selected, setSelected] = useState(lastSelectedUsageExportType || usageExportTypes.DEFAULT_EXCEL);
  const [fetching, setFetching] = useState(false);
  const [unleashedLoading, setUnleashedLoading] = useState(false);

  const [unleashedSalesOrder, setUnleashedSalesOrder] = useState(null);

  useEffect(() => {
    if (open) {
      fetchUnleashedData().catch();
    }
  }, [open]);

  const fetchUnleashedData = async () => {
    try {
      setFetching(true);
      const order = await dispatch(getUnleashedSalesOrder(activeCase.id));
      if (order) {
        setUnleashedSalesOrder(order);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setFetching(false);
    }
  }

  const onSendUsageToUnleashed = async () => {
    if (!!unleashedSalesOrder) {
      return;
    }

    try {
      setUnleashedLoading(true);
      const res = await dispatch(exportUsageToUnleashed(activeCase.id));
      if (res && res.success) {
        const order = await dispatch(getUnleashedSalesOrder(activeCase.id));
        if (order) {
          setUnleashedSalesOrder(order);
        }
        await dispatch(getCase(activeCase?.id));
      } else {
        const parsed = JSON.parse(res.error);
        const description = parsed.Items[0].Description
        showAlert('error', description);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setUnleashedLoading(false);
    }
  };

  const handleSubmit = () => {
    onSubmit(selected);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title='Export Usage'
      fullWidth
      maxWidth='lg'
    >
      <div style={{ padding: '0 24px 24px' }}>
        <div className='m-b-lg'>
          {fetching ? (
            <div style={{ marginTop: 24 }} className='d-flex flex-center'>
              <CircularProgress />
            </div>
          ) : (
            <ExportUsageUnleashed
              unleashedSalesOrder={unleashedSalesOrder}
              loading={unleashedLoading}
              onSendUsageToUnleashed={onSendUsageToUnleashed}
              usageIntegrationData={activeCase?.usageIntegrationData?.unleashed}
              users={users}
            />
          )}
        </div>

        <div className='m-t-lg'>
          <div className='secondary m-b-sm'>Select to Export</div>
          <div className='dispatch-modal-checkboxes__container'>
            {options?.map((opt) => (
              <Checkbox
                key={opt.key}
                label={opt.label}
                input={{
                  checked: selected === opt.value,
                  onChange: (e) => {
                    if (e?.target?.checked) {
                      setSelected(opt.value);
                    } else {
                      setSelected(null);
                    }
                  }
                }}
                fontSize='small'
              />
            ))}
          </div>
        </div>

        <div className='form__actions p-t-lg'>
          <Button
            type='submit'
            text='Export'
            onClick={handleSubmit}
            disabled={loading || !selected}
            loading={loading}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ExportUsageModal;
